
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AnalysisWithProcurement } from '@/types/analysis';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Download, Info } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

type AnalysisCardProps = {
  analysis: AnalysisWithProcurement;
};

const AnalysisCard = ({ analysis }: AnalysisCardProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleDownload = () => {
    toast({
      title: "Nedladdning",
      description: "Rapporten laddas ner...",
    });
  };

  return (
    <Card key={analysis.id} className="overflow-hidden">
      <CardHeader className="pb-3 border-b bg-gray-50">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg">{analysis.procurement.title}</CardTitle>
            <CardDescription className="mt-1">
              {analysis.procurement.authority} • Analyserad {new Date(analysis.created_at).toLocaleDateString('sv-SE')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate(`/analyses/${analysis.id}`)}
            >
              <ExternalLink size={16} className="mr-2" />
              Visa fullständig analys
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleDownload}
            >
              <Download size={16} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex flex-col items-center justify-center p-4 bg-yellow-50 rounded-lg">
            <div className="text-3xl font-bold text-accent mb-1">{analysis.score || 85}%</div>
            <div className="text-sm text-gray-600">Matchningspoäng</div>
          </div>
          
          <div className="col-span-3 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Affärsmöjlighet</div>
                <div className="font-medium">{analysis.opportunities || 'Hög'}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Komplexitet</div>
                <div className="font-medium">{analysis.complexity || 'Medium'}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Konkurrens</div>
                <div className="font-medium">{analysis.competition || 'Medium'}</div>
              </div>
            </div>
            
            <div>
              <div className="text-sm font-medium mb-2">Nyckelrekommendationer</div>
              <div className="space-y-2">
                {(analysis.recommendations ? 
                  JSON.parse(analysis.recommendations) : 
                  [
                    "Fokusera på miljöcertifieringar och hållbarhetsaspekter i anbudet",
                    "Presentera detaljerad tidplan med tydliga milstolpar",
                    "Framhäv tidigare erfarenhet av liknande projekt med offentliga aktörer"
                  ]
                ).map((rec: string, index: number) => (
                  <div key={index} className="flex items-start gap-2">
                    <Info size={16} className="text-accent mt-0.5 shrink-0" />
                    <div className="text-sm">{rec}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AnalysisCard;
